/**
 * Shop full-text search.
 *
 * Wires up any [data-shop-search-input] in the DOM with an instant-search
 * dropdown backed by MiniSearch. The library and the per-language index
 * JSON are lazy-loaded the first time the user types — no network or
 * eval cost until then.
 */

const STATE = {
  loadingByLang: new Map(),
  msByLang: new Map(),
};

// ---------------------------------------------------------------------------
// Tokenization: a tiny rule-based German stemmer. Diacritic-folds the input
// (so a query of "Trager" still matches "Träger"), then strips common
// inflection suffixes. Operates on both index terms and query terms via
// MiniSearch's processTerm hook so the two stay in lockstep.
// ---------------------------------------------------------------------------
const SUFFIXES = ["ungen", "ung", "ern", "en", "er", "em", "es", "e", "n", "s"];

function foldDiacritics(s) {
  return s
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/ß/g, "ss");
}

function stemDe(term) {
  let w = foldDiacritics(term.toLowerCase());
  for (const suf of SUFFIXES) {
    if (w.length - suf.length >= 4 && w.endsWith(suf)) {
      w = w.slice(0, -suf.length);
      break;
    }
  }
  return w;
}

// ---------------------------------------------------------------------------
// Lazy load: import MiniSearch + fetch the right index, build the instance.
// Cached per language so EN and DE pages on the same SPA-ish session both
// stay snappy.
// ---------------------------------------------------------------------------
// Loaded as a separate JS file (not bundled into shop.min.js) so the lib
// only goes over the wire when a user actually starts typing in the
// search input. The variable indirection prevents esbuild from inlining
// the import at bundle time.
const MINISEARCH_URL = "/js/minisearch.min.js";

async function ensureIndex(lang) {
  const cached = STATE.msByLang.get(lang);
  if (cached) {
    return cached;
  }
  const inFlight = STATE.loadingByLang.get(lang);
  if (inFlight) {
    return inFlight;
  }

  const promise = (async () => {
    const [mod, response] = await Promise.all([
      import(/* @vite-ignore */ MINISEARCH_URL),
      fetch(`/search/products.${lang}.json`),
    ]);
    const MiniSearch = mod.default || mod.MiniSearch;
    if (!response.ok) {
      throw new Error(`search index fetch failed: ${response.status}`);
    }
    const records = await response.json();

    const ms = new MiniSearch({
      idField: "id",
      fields: ["title", "subtitle", "description", "tags", "collections", "body"],
      storeFields: [
        "id",
        "title",
        "subtitle",
        "price_from",
        "image",
        "url",
        "available",
      ],
      processTerm: (term) => {
        const stem = stemDe(term);
        return stem || false;
      },
      tokenize: (text) => text.split(/[^\p{L}\p{N}]+/u).filter(Boolean),
    });

    ms.addAll(records);
    STATE.msByLang.set(lang, ms);
    return ms;
  })();

  STATE.loadingByLang.set(lang, promise);
  try {
    return await promise;
  } finally {
    STATE.loadingByLang.delete(lang);
  }
}

// ---------------------------------------------------------------------------
// Search + render
// ---------------------------------------------------------------------------
const MAX_RESULTS = 8;

function runSearch(ms, query) {
  return ms
    .search(query, {
      boost: {
        title: 4,
        subtitle: 2,
        tags: 3,
        collections: 2,
        description: 1,
        body: 0.5,
      },
      prefix: true,
      fuzzy: 0.2,
      combineWith: "AND",
    })
    .slice(0, MAX_RESULTS);
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => {
    return {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    }[c];
  });
}

function thumbUrl(image) {
  if (!image) {
    return "";
  }
  // Convention: every shop image foo.avif has a foo-thumb.avif (200x200)
  return `/img/${image.replace(/\.avif$/, "-thumb.avif")}`;
}

function renderResults(dropdown, results, lang, selectedIdx) {
  if (!results.length) {
    dropdown.innerHTML = `<p class="shop-search__empty">${
      lang === "en"
        ? "No products match your search."
        : "Keine passenden Produkte gefunden."
    }</p>`;
    return;
  }

  dropdown.innerHTML = results
    .map((r, i) => {
      const img = thumbUrl(r.image);
      return `<a href="${escapeHtml(r.url)}"
         id="shop-search-result-${i}"
         class="shop-search__result${i === selectedIdx ? " is-selected" : ""}"
         role="option"
         aria-selected="${i === selectedIdx ? "true" : "false"}"
         data-result-index="${i}">
        ${img ? `<img class="shop-search__thumb" src="${img}" alt="" width="56" height="56" loading="lazy" decoding="async">` : ""}
        <span class="shop-search__text">
          <strong>${escapeHtml(r.title)}</strong>
          ${r.subtitle ? `<span class="shop-search__sub">${escapeHtml(r.subtitle)}</span>` : ""}
        </span>
        ${r.price_from ? `<span class="shop-search__price">${escapeHtml(r.price_from)}</span>` : ""}
      </a>`;
    })
    .join("");
}

// ---------------------------------------------------------------------------
// Per-input wiring
// ---------------------------------------------------------------------------
function bindInput(input, lang) {
  const wrapper = input.closest("[data-shop-search]") || input.parentElement;
  const dropdown = wrapper.querySelector("[data-shop-search-results]");
  if (!dropdown) {
    return;
  }

  let results = [];
  let selectedIdx = -1;
  let debounce = null;

  function open() {
    dropdown.hidden = false;
    input.setAttribute("aria-expanded", "true");
  }

  function close() {
    dropdown.hidden = true;
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
    selectedIdx = -1;
  }

  function applySelection() {
    const items = dropdown.querySelectorAll("[data-result-index]");
    items.forEach((el, i) => {
      const sel = i === selectedIdx;
      el.setAttribute("aria-selected", sel ? "true" : "false");
      el.classList.toggle("is-selected", sel);
    });
    if (selectedIdx >= 0) {
      input.setAttribute(
        "aria-activedescendant",
        `shop-search-result-${selectedIdx}`,
      );
    } else {
      input.removeAttribute("aria-activedescendant");
    }
  }

  async function onQueryChange() {
    const q = input.value.trim();
    if (!q) {
      close();
      return;
    }
    let ms;
    try {
      ms = await ensureIndex(lang);
    } catch (err) {
      // Fail soft: leave the dropdown closed, surface error to console
      console.error("shop-search index load failed", err);
      close();
      return;
    }
    // The user may have cleared the input while the index was loading
    if (!input.value.trim()) {
      close();
      return;
    }
    results = runSearch(ms, input.value.trim());
    selectedIdx = -1;
    renderResults(dropdown, results, lang, selectedIdx);
    open();
  }

  input.addEventListener("input", () => {
    clearTimeout(debounce);
    debounce = setTimeout(onQueryChange, 80);
  });

  input.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      close();
      input.blur();
      return;
    }
    if (e.key === "ArrowDown" && results.length) {
      e.preventDefault();
      selectedIdx = (selectedIdx + 1) % results.length;
      applySelection();
      return;
    }
    if (e.key === "ArrowUp" && results.length) {
      e.preventDefault();
      selectedIdx =
        selectedIdx <= 0 ? results.length - 1 : selectedIdx - 1;
      applySelection();
      return;
    }
    if (e.key === "Enter") {
      if (selectedIdx >= 0 && results[selectedIdx]) {
        e.preventDefault();
        window.location.href = results[selectedIdx].url;
      }
    }
  });

  // Click outside closes
  document.addEventListener("click", (e) => {
    if (!wrapper.contains(e.target)) {
      close();
    }
  });

  // Pointer down on a result navigates before the input's blur fires
  dropdown.addEventListener("mousedown", (e) => {
    const target = e.target.closest("[data-result-index]");
    if (target) {
      e.preventDefault();
      window.location.href = target.getAttribute("href");
    }
  });
}

// ---------------------------------------------------------------------------
// Public init
// ---------------------------------------------------------------------------
export function init() {
  const inputs = document.querySelectorAll("[data-shop-search-input]");
  if (!inputs.length) {
    return;
  }

  const lang =
    document.documentElement.lang &&
    document.documentElement.lang.toLowerCase().startsWith("en")
      ? "en"
      : "de";

  inputs.forEach((input) => bindInput(input, lang));

  // Cmd-K / Ctrl-K: focus the first search input on any shop page
  document.addEventListener("keydown", (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
      const target = document.querySelector("[data-shop-search-input]");
      if (!target) {
        return;
      }
      e.preventDefault();
      target.focus();
      target.select();
    }
  });
}
